import { useState } from "react";
import type { GetProfileCandidateMeetingsQueryQuery } from "types/graphql-schema";

import MeetingDialog from "@apps/meeting-dialog/meeting-dialog";
import useLabel from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";

import getProfileOneononeQuery from "../graphql/get-profile-oneonone-query";
import SelectOneononeMeetingDialog from "./select-oneonone-meeting-dialog";

type CandidateMeeting = NonNullable<
  NonNullable<
    NonNullable<
      GetProfileCandidateMeetingsQueryQuery["candidateMeetings"]
    >["edges"][0]
  >["node"]
>;

const ProfileNoOneononeSetup = ({
  profileId,
  profileName,
  profileFirstName,
  profileEmail,
  candidateMeetings,
}: {
  profileId: number;
  profileName: string;
  profileFirstName: string;
  profileEmail: string;
  candidateMeetings: CandidateMeeting[];
}) => {
  const label = useLabel();
  const currentUser = currentUserVar();
  const [showCreateMeeting, setShowCreateMeeting] = useState(false);
  const [showSelectMeeting, setShowSelectMeeting] = useState(false);

  const hasCandidates = candidateMeetings.length > 0;

  // Generate auto-title: "Manager / Report" or "CurrentUser / ProfileUser"
  const autoTitle = `${currentUser.firstName} / ${profileFirstName}`;

  const profile = {
    id: profileId,
    name: profileName,
    email: profileEmail,
  };

  const renderEmptyCalendar = (text: string, className = "") => (
    <div
      className={classNames(
        "bg-gray-50 border border-gray-100 rounded-md p-6 flex flex-col items-center gap-3",
        className,
      )}
    >
      <TopicflowIcon type="calendarPlus" className="w-6 h-6 text-blue-500" />
      <div className="flex flex-col items-center gap-1 text-center">
        <div className="font-semibold text-sm text-gray-800">
          Create a recurring {label("1-on-1")}
        </div>
        <div className="text-sm text-gray-600">{text}</div>
      </div>
      <Button
        theme={buttonTheme.default}
        onClick={() => setShowCreateMeeting(true)}
        text={`Create ${label("1-on-1")}`}
      />
    </div>
  );

  return (
    <>
      <div className="flex flex-col gap-4 border rounded-md p-6">
        {/* Header */}
        <div>
          <div className="tf-header-base-dark">
            You do not have a recurring {label("1-on-1")} setup with{" "}
            {profileFirstName}
          </div>
          <div className="text-sm text-gray-600">
            With a recurring {label("1-on-1")}, you will get coaching insights,
            suggestions and AI summaries every meeting.
          </div>
        </div>

        {/* Action cards */}
        {hasCandidates ? (
          <div className="flex gap-3">
            {/* Select existing meeting card */}

            <div className="flex-1 bg-gray-50 border border-gray-100 rounded-md p-6 flex flex-col items-center gap-3">
              <TopicflowIcon
                type="calendarCheck"
                className="w-6 h-6 text-blue-500"
              />
              <div className="flex flex-col items-center gap-1 text-center">
                <div className="font-semibold text-sm text-gray-800">
                  Select a recurring meeting
                </div>
                <div className="text-sm text-gray-600">
                  We found some recurring meetings with you and{" "}
                  {profileFirstName}, do you want to select one?
                </div>
              </div>
              <Button
                theme={buttonTheme.primary}
                onClick={() => setShowSelectMeeting(true)}
                text="Select Meeting"
              />
            </div>

            {/* Create new 1:1 card */}
            {renderEmptyCalendar(
              `Instead of selecting one of the existing meetings, start fresh with a new ${label("1-on-1")}.`,
              "flex-1",
            )}
          </div>
        ) : (
          /* No candidate meetings - just show create */
          renderEmptyCalendar(
            `We couldn't find any existing meetings to convert into a ${label("1-on-1")}, get started by creating a new one.`,
          )
        )}
      </div>

      {/* Create meeting dialog */}
      {showCreateMeeting && (
        <MeetingDialog
          formOptions={{
            attendees: [profile, currentUser],
            isFormalOneonone: true,
            title: autoTitle,
          }}
          refetchQueries={[getProfileOneononeQuery]}
          onSaved={() => setShowCreateMeeting(false)}
          onClose={() => setShowCreateMeeting(false)}
        />
      )}

      {/* Select existing meeting dialog */}
      {showSelectMeeting && (
        <SelectOneononeMeetingDialog
          candidateMeetings={candidateMeetings}
          profileFirstName={profileFirstName}
          onClose={() => setShowSelectMeeting(false)}
        />
      )}
    </>
  );
};

export default ProfileNoOneononeSetup;
